import { useEffect, useRef } from "react";
import { getLang, trKnown, subscribeMap } from "../lib/i18n";

const ATTRS = ["placeholder", "title", "aria-label"];
const SKIP = "script, style, textarea, input, [translate='no'], [data-no-translate], code, pre";

// Translates static UI copy inside this subtree from the shipped dictionary only (never sends unknown text anywhere).
// Mark personal content (names, document titles, notes) with translate="no".
export const AutoTranslate = ({ children, as: Tag = "div", className }) => {
  const ref = useRef(null);
  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const orig = new WeakMap();
    let busy = false;
    const run = () => {
      if (busy) return;
      busy = true;
      const es = getLang() !== "en";
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, { acceptNode: (n) => (n.parentElement?.closest(SKIP) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT) });
      for (let n = walker.nextNode(); n; n = walker.nextNode()) {
        const rec = orig.get(n);
        const src = rec && n.nodeValue === rec.out ? rec.src : n.nodeValue;
        if (!/[A-Za-z]/.test(src)) continue;
        const out = es ? trKnown(src) : src;
        if (out !== n.nodeValue) n.nodeValue = out;
        orig.set(n, { src, out });
      }
      root.querySelectorAll(ATTRS.map((a) => `[${a}]`).join(",")).forEach((el) => {
        if (el.closest("[translate='no'], [data-no-translate]")) return;
        ATTRS.forEach((a) => {
          const v = el.getAttribute(a);
          if (!v || !/[A-Za-z]/.test(v)) return;
          const src = el.dataset[`hvsrc${a.replace("-", "")}`] && el.dataset[`hvout${a.replace("-", "")}`] === v ? el.dataset[`hvsrc${a.replace("-", "")}`] : v;
          const out = es ? trKnown(src) : src;
          el.dataset[`hvsrc${a.replace("-", "")}`] = src;
          el.dataset[`hvout${a.replace("-", "")}`] = out;
          if (out !== v) el.setAttribute(a, out);
        });
      });
      busy = false;
    };
    run();
    const mo = new MutationObserver(() => requestAnimationFrame(run));
    mo.observe(root, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ATTRS });
    const unsub = subscribeMap(run);
    return () => { mo.disconnect(); unsub(); };
  }, []);
  return <Tag ref={ref} className={className} style={className ? undefined : { display: "contents" }}>{children}</Tag>;
};
